<template>
  <div class="best-article">
    <TestPixel
      v-for="(testPixel, index) in article.test_pixels"
      :key="index"
      :pixel-link="testPixel"
    />
    <BHLink
      :aria-label="`Перейти к статье ${article.title}`"
      :href="article.url"
      class="best-article__link"
      @click="sendAnalytics"
    />
    <div
      class="best-article__title"
      @click="sendAnalytics"
      v-html="article.title"
    />
    <div class="best-article__footer">
      <TheReaction
        v-if="isShowReactions"
        :article="article"
        analytics-event="Клик_Реакции_Лучшее за неделю"
        is-simple-reaction
      />
      <CommentsCrumb
        v-if="isShowComments"
        :comments-count="article.comments_count"
        :href="`${article.url}#comments`"
        @click-comments-icon="sendAnalyticsComment"
      />
      <time
        class="best-article__date"
        :datetime="dateMoscow"
        >{{ date }}</time
      >
    </div>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import type { TArticle } from '~/types/common';
  import { getDateText } from '~/utils/date';
  import BHLink from '~/components/BHLink.vue';
  import TestPixel from '~/components/TestPixel.vue';
  import getSlugFromUrl from '~/utils/getSlugFromUrl';
  import CommentsCrumb from '@/components/CommentsCrumb.vue';
  import TheReaction from '@/components/Reaction/TheReaction.vue';
  import { useServiceErrorsStore } from '~/store/errors';
  import { convertBackendTimestampToMoscowString } from '@devhacker/shared/utils/date';

  export default defineNuxtComponent({
    name: 'BestArticlesWidgetItem',
    components: {
      BHLink,
      TestPixel,
      CommentsCrumb,
      TheReaction,
    },

    inject: {
      // Категория в которой происходит событие
      // Для аналитики
      eventCategory: {
        default() {
          return () => '';
        },
      },
    },

    props: {
      article: {
        type: Object as PropType<TArticle>,
        required: true,
      },
    },

    computed: {
      isReactionServiceError() {
        return useServiceErrorsStore().reactionsServiceError;
      },

      isCommentsServiceError() {
        return useServiceErrorsStore().commentsServiceError;
      },

      date(): string {
        return this.getDateText(this.article.date);
      },

      dateMoscow(): string {
        return convertBackendTimestampToMoscowString(this.article.date);
      },

      currentSlug(): string {
        return getSlugFromUrl(useRoute().fullPath);
      },

      isShowReactions(): boolean {
        if (this.isReactionServiceError) {
          return false;
        }

        return (
          !this.article.is_promo && !this.article.is_advertising && this.article?.reactions_enabled
        );
      },

      isShowComments(): boolean {
        if (this.isCommentsServiceError || !this.article.comments_count) {
          return false;
        }

        return this.article.comments_enabled && !this.article.is_promo;
      },
    },

    methods: {
      getDateText,
      sendAnalytics() {
        this.$sendYandexMetrika({
          level1: 'Выбор материала_лучшее за неделю',
          level4: this.article.url,
        });
      },

      sendAnalyticsComment(url: string) {
        this.$sendYandexMetrika({
          level1: 'Комментарии_переход к комментариям',
          level4: url,
          level6: 'Лучшее за неделю',
        });
      },
    },
  });
</script>

<style lang="scss" scoped>
  $button-icon-size: 24px;

  .best-article {
    padding: $spacing-4 $spacing-6;
    border-radius: 28px;
    position: relative;

    @include hover {
      &::before {
        position: absolute;
        content: '';
        left: 0px;
        top: 0px;
        border: 4px solid $hover-color--1;
        border-radius: 28px;
        height: 100%;
        width: 100%;
      }

      .best-article {
        &__title {
          color: $hover-color--1;
        }
      }
    }

    &__link {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
    }

    &__title {
      @include mediumText;
      font-weight: 400;
    }

    &__date {
      display: block;
      color: $icon-color--1;
      @include smallText;
    }

    &__footer {
      margin-top: $spacing-2;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    :deep() {
      .reaction-button__icon--button {
        width: $button-icon-size;
        height: $button-icon-size;
        background-size: $button-icon-size !important;
      }
      .comments-crumb__icon {
        min-width: $button-icon-size;
        min-height: $button-icon-size;
        max-width: $button-icon-size;
        max-height: $button-icon-size;
      }
      .comments-crumb__count {
        margin-left: 2px;
      }
      .reaction-container {
        margin-right: 0px;
      }
      .reaction-button {
        padding: 0;
        &--heart,
        &--thumbs_up,
        &--on_fire,
        &--cry,
        &--angry {
          background: none;
        }
      }
    }
  }
</style>
